// src/component/datatable2/models/FilterCriteriaModel.ts
//
// Immutable changes of DataTable2's search criteria (filters, paging, global
// search). Every method returns a new object and never mutates its input,
// so they can be passed straight to setSearchCriteria(prev => ...).

import { FILTER_TYPES } from '../constants/FilterTypes';

export interface CriteriaFilter {
    type?: string;
    value?: any;
    [key: string]: any;
}

export interface SearchCriteria {
    first?: number;
    rows?: number;
    filters?: Record<string, CriteriaFilter>;
    [key: string]: any;
}

/** One removable chip of an active filter (see ActiveFilterChips). */
export interface FilterChipRef {
    name: string;
    value: any;
}

export class FilterCriteriaModel {
    /**
     * True when a filter value filters nothing: null/undefined, '', an empty
     * array, or a range object with neither from nor to.
     */
    static isEmptyValue(value: unknown): boolean {
        if (value === null || value === undefined || value === '') {
            return true;
        }

        if (Array.isArray(value)) {
            return value.length === 0;
        }

        if (typeof value === 'object') {
            const range = value as { from?: unknown; to?: unknown };

            return range.from == null && range.to == null;
        }

        return false;
    }

    /** Filters without the empty ones (and without missing entries). */
    static clean(filters: Record<string, CriteriaFilter | null | undefined>): Record<string, CriteriaFilter> {
        const result: Record<string, CriteriaFilter> = {};

        Object.keys(filters).forEach(key => {
            const filter = filters[key];

            if (filter && !FilterCriteriaModel.isEmptyValue(filter.value)) {
                result[key] = filter;
            }
        });

        return result;
    }

    /** Sets (or, when its value is empty, removes) the filter of a field; back to the first page. */
    static withFilter<T extends SearchCriteria>(criteria: T, fieldName: string, filter: CriteriaFilter): T {
        return {
            ...criteria,
            first: 0,
            filters: FilterCriteriaModel.clean({ ...criteria.filters, [fieldName]: filter }),
        };
    }

    /** Removes the filter of a field; back to the first page. */
    static withoutFilter<T extends SearchCriteria>(criteria: T, fieldName: string): T {
        const filters = { ...criteria.filters };

        delete filters[fieldName];

        return { ...criteria, first: 0, filters };
    }

    /**
     * Removes one chip: one option of a list / multi-value text filter, or the
     * whole filter for other types. A list filter stays (possibly empty), as
     * DataTable2 always did; a text filter left without options is removed.
     */
    static withoutChip<T extends SearchCriteria>(criteria: T, chip: FilterChipRef): T {
        const filters = { ...criteria.filters };
        const target = filters[chip.name];

        if (target?.type === FILTER_TYPES.LIST) {
            filters[chip.name] = {
                ...target,
                value: (target.value ?? []).filter((item: any) => item.id !== chip.value?.id),
            };
        } else if (target?.type === FILTER_TYPES.TEXT && Array.isArray(target.value)) {
            const next = target.value.filter((item: any) => item.id !== chip.value?.id);

            if (next.length > 0) {
                filters[chip.name] = { ...target, value: next };
            } else {
                delete filters[chip.name];
            }
        } else {
            delete filters[chip.name];
        }

        return { ...criteria, first: 0, filters };
    }

    /** Removes every filter; back to the first page. */
    static withoutFilters<T extends SearchCriteria>(criteria: T): T {
        return { ...criteria, first: 0, filters: {} };
    }

    static withPage<T extends SearchCriteria>(criteria: T, first: number, rows: number): T {
        return { ...criteria, first, rows };
    }

    static withBaseQuery<T extends SearchCriteria>(criteria: T, baseQuery: string): T {
        return { ...criteria, baseQuery, first: 0 };
    }

    /** First row index of a 1-based page. */
    static firstRowOf(page: number, rows: number): number {
        return rows * (page - 1);
    }

    /** 1-based page of a first row index. */
    static pageOf(first: number, rows: number): number {
        return Math.floor(first / rows) + 1;
    }
}
